import { DeprecatedIcon } from '@fullstory/components';
import { classNames } from '@fullstory/util/fmt';
import React from 'react';
import { Link } from 'react-router-dom';
import css from './index.module.scss';
import { useFlags } from '@fullstory/react-hooks';

export interface PlayButtonProps {
  isOnline: boolean;
  isWatched: boolean;
  isClip: boolean;
  to: React.ComponentProps<typeof Link>['to'];
  onClick?: () => void;
  newTab?: boolean;
  disabled?: boolean;
  testId?: string;
}

export const PlayButton: React.FC<PlayButtonProps> = ({
  to,
  isOnline,
  isWatched,
  isClip,
  onClick,
  newTab,
  disabled = false,
  testId,
}) => {
  const newFSTAColors = useFlags().featureFlags().AppNewFstaColors;
  let baseIcon: React.ComponentProps<typeof DeprecatedIcon>['name'] = 'playback/play';
  let hoveredIcon: React.ComponentProps<typeof DeprecatedIcon>['name'] = 'playback/play';

  if (isClip) {
    baseIcon = isWatched ? 'playback/clip-watched' : 'playback/clip';
    hoveredIcon = 'playback/clip-hover';
  } else if (isWatched) {
    baseIcon = 'playback/play-outline';
  }

  if (disabled) {
    baseIcon = 'playback/play';
  }

  return (
    <Link
      data-component="PlayButton"
      className={classNames(css.wrapper, {
        [css.border]: !isClip,
        [css.onlineBorder]: isOnline && !isClip,
        [css.disabled]: disabled,
      })}
      to={to}
      onClick={onClick}
      data-testid={testId || 'play-button'}
      target={newTab ? '_blank' : '_self'}
      data-is-clip={isClip}
      data-is-online={isOnline}
      data-is-watched={isWatched}
      data-fs-properties-schema='{"href":"str","data-is-clip":"bool","data-is-online":"bool","data-is-watched":"bool"}'
    >
      <div
        className={classNames(css.baseLayer, {
          [css.online]: !isClip && isOnline && !disabled,
          [css.notOnline]: !isClip && !isOnline && !disabled,
          [css.clip]: isClip && !disabled,
          [css.disabled]: disabled,
          [css.watched]: isWatched,
          [css.newFSTAColors]: newFSTAColors,
        })}
      >
        <DeprecatedIcon
          name={baseIcon}
          size={isClip ? 40 : 24}
          grammarUIPropOverrides={{ color: 'inherit' }}
        />
      </div>
      <div
        className={classNames(css.hoveredLayer, {
          [css.online]: !isClip && isOnline && !disabled,
          [css.notOnline]: !isClip && !isOnline && !disabled,
          [css.clip]: isClip && !disabled,
          [css.disabled]: disabled,
          [css.newFSTAColors]: newFSTAColors,
        })}
      >
        <DeprecatedIcon
          name={hoveredIcon}
          size={isClip ? 40 : 24}
          grammarUIPropOverrides={{ color: 'inherit' }}
        />
      </div>
    </Link>
  );
};
PlayButton.displayName = 'PlayButton';
